import { createFileRoute } from '@tanstack/react-router';
import { useLingui } from '@lingui/react/macro';
import { useSuspenseQuery } from '@tanstack/react-query';
import { Suspense } from 'react';
import { trpc } from '@/lib/trpc';
import { Header } from './-components/header';
import { Footer } from './-components/footer';
import { Layout } from './-components/layout';
import { PageSeo } from './-components/page-seo';
import { slugs } from './index.meta';
import { Hero } from './-components/home/hero';
import { Solutions } from './-components/home/solutions';
import { Marquee } from './-components/home/marquee';
import { Partners } from './-components/partners';
import { RecentCampaigns } from './-components/home/recent-campaigns';
import { MoreAbout } from './-components/home/more-about';
import { AppDownload } from './-components/home/app-download';

export const Route = createFileRoute('/www/')({
	loader: ({ context }) => {
		// `prefetchQuery`, never `ensureQueryData`: an unawaited ensure rejects with `CancelledError` when a
		// visitor leaves mid-SSR, and that unhandled rejection takes the whole server process down.
		context.queryClient.prefetchQuery(trpc.portal.getWorldcooGlobalStats.queryOptions());
		context.queryClient.prefetchQuery(trpc.portal.getRecentCampaigns.queryOptions({ locale: context.locale }));
	},
	component: Component,
});

/** Every figure on this page and its hour of cache: docs/notes/portals/www-live-figures.md */
function PartnersSection() {
	const { data: stats } = useSuspenseQuery(trpc.portal.getWorldcooGlobalStats.queryOptions());
	return <Partners merchants={stats.partnerMerchants} />;
}

function RecentCampaignsSection() {
	const { locale } = Route.useRouteContext();
	const { data } = useSuspenseQuery(trpc.portal.getRecentCampaigns.queryOptions({ locale }));
	const { data: stats } = useSuspenseQuery(trpc.portal.getWorldcooGlobalStats.queryOptions());
	return <RecentCampaigns campaigns={data.campaigns} ngoCount={stats.socialOrganizations} />;
}

function Component() {
	const { t } = useLingui();
	const { baseUrl } = Route.useRouteContext();
	const description = t`We connect companies, consumers and NGOs through transparent fundraising technology to finance social projects all over the world.`;

	// They describe the ORGANISATION, so a knowledge panel is built from them; the archived host is
	// `noindex`, so leaving them there would have retired them (TEC-3770).
	const jsonLd = [
		{
			'@context': 'https://schema.org',
			'@type': 'Organization',
			'name': 'Worldcoo',
			'url': baseUrl,
			'description': description,
			'logo': 'https://cdn.worldcoo.com/public/img/worldcoo-icon-dark.png',
		},
		{
			'@context': 'https://schema.org',
			'@type': 'WebSite',
			'name': 'Worldcoo',
			'url': baseUrl,
		},
	];

	return (
		<Layout>
			<Header />
			<PageSeo
				slugs={slugs}
				title={t`Worldcoo - Shopping that builds a better world`}
				description={description}
				jsonLd={jsonLd}
			/>
			<Hero />
			<Solutions />
			<Marquee />
			{/* Each section is its own fallback: fixed boxes were 270px and 450px short, dropping the page by
			    three quarters of a screen when the queries landed. */}
			<Suspense fallback={<Partners />}>
				<PartnersSection />
			</Suspense>
			<Suspense fallback={<RecentCampaigns />}>
				<RecentCampaignsSection />
			</Suspense>
			<MoreAbout />
			<AppDownload />
			<Footer />
		</Layout>
	);
}
